import { useState, useEffect, useCallback } from "react";
import axios from "axios";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Input } from "@/components/ui/input";
import { ScrollArea } from "@/components/ui/scroll-area";
import {
  Twitter, Search, Loader2, MessageCircle, Heart, Repeat2,
  TrendingUp, Lightbulb, Clock, BarChart3, Smile, Frown, Meh
} from "lucide-react";
import { ModuleDescription } from "@/components/ModuleDescription";

const API = process.env.REACT_APP_BACKEND_URL + "/api";

const SentimentIcon = ({ sentiment }) => {
  if (sentiment === "pozytywny") return <Smile className="w-5 h-5 text-green-500" />;
  if (sentiment === "negatywny") return <Frown className="w-5 h-5 text-red-500" />;
  return <Meh className="w-5 h-5 text-amber-500" />;
};

const SentimentBar = ({ score }) => {
  const color = score > 60 ? "bg-green-500" : score < 40 ? "bg-red-500" : "bg-amber-500";
  return (
    <div className="w-full bg-muted rounded-full h-2">
      <div className={`h-2 rounded-full ${color} transition-all`} style={{ width: `${score}%` }} />
    </div>
  );
};

export default function TwitterAnalysis() {
  const [query, setQuery] = useState("");
  const [analyzing, setAnalyzing] = useState(false);
  const [result, setResult] = useState(null);
  const [history, setHistory] = useState([]);
  const [loadingHistory, setLoadingHistory] = useState(true);

  const fetchHistory = useCallback(async () => {
    try {
      const res = await axios.get(`${API}/twitter/history`);
      setHistory(res.data.analyses || []);
    } catch {}
    setLoadingHistory(false);
  }, []);

  useEffect(() => { fetchHistory(); }, [fetchHistory]);

  const analyze = async () => {
    if (!query.trim()) return;
    setAnalyzing(true);
    setResult(null);
    try {
      const res = await axios.post(`${API}/twitter/analyze`, { query: query.trim(), limit: 15 });
      setResult(res.data);
      fetchHistory();
    } catch {}
    setAnalyzing(false);
  };

  const loadFromHistory = (item) => {
    setResult(item);
    setQuery(item.query || "");
  };

  const a = result?.analysis || {};

  return (
    <div className="space-y-6" data-testid="twitter-analysis-page">
      <div>
        <h1 className="text-3xl font-bold tracking-tight flex items-center gap-2">
          <Twitter className="w-8 h-8 text-sky-500" /> Analiza X / Twitter
        </h1>
        <p className="text-muted-foreground text-sm mt-1">
          Wyszukaj dyskusje na X, przeanalizuj sentyment i wyciagnij insighty
        </p>
      </div>
      <ModuleDescription title="Jak dziala analiza X/Twitter?">
        <p><strong>Analiza X/Twitter</strong> scrapuje dyskusje na platformie X (dawniej Twitter) na dowolny temat i analizuje sentyment przez AI.</p>
        <p><strong>Jak to dziala:</strong> Wpisz temat (np. "inflacja Polska", "bitcoin kurs"). System przeszuka X, pobierze tweety i przeanalizuje je przez Grok AI.</p>
        <p><strong>Wyniki:</strong> Sentyment (pozytywny/negatywny/mieszany), kluczowe opinie, pomysly na artykuly.</p>
        <p><strong>Historia</strong> po prawej = poprzednie analizy. Kliknij aby zaladowac wyniki.</p>
        <p><strong>Automatyzacja:</strong> SEO Agent co 12h automatycznie monitoruje Google Trends i X/Twitter szukajac goracych tematow z potencjalem.</p>
      </ModuleDescription>


      {/* Search */}
      <Card>
        <CardContent className="p-4">
          <div className="flex gap-2">
            <div className="relative flex-1">
              <Search className="absolute left-3 top-2.5 w-4 h-4 text-muted-foreground" />
              <Input
                placeholder="Wpisz temat, fraze lub hashtag..."
                value={query}
                onChange={e => setQuery(e.target.value)}
                onKeyDown={e => e.key === "Enter" && analyze()}
                className="pl-10"
                data-testid="twitter-search-input"
              />
            </div>
            <Button
              onClick={analyze}
              disabled={analyzing || !query.trim()}
              className="gap-1.5 bg-sky-600 hover:bg-sky-700"
              data-testid="twitter-analyze-btn"
            >
              {analyzing ? <Loader2 className="w-4 h-4 animate-spin" /> : <Search className="w-4 h-4" />}
              {analyzing ? "Analizuje..." : "Analizuj"}
            </Button>
          </div>
        </CardContent>
      </Card>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        {/* Results */}
        <div className="lg:col-span-2 space-y-4">
          {analyzing && (
            <Card>
              <CardContent className="p-8 text-center">
                <Loader2 className="w-10 h-10 mx-auto mb-3 animate-spin text-sky-500" />
                <p className="text-sm text-muted-foreground">Scrapuje X i analizuje sentyment...</p>
              </CardContent>
            </Card>
          )}

          {result && !analyzing && (
            <>
              {/* Summary */}
              <Card className="border-sky-200">
                <CardContent className="p-5">
                  <div className="flex items-start gap-4">
                    <SentimentIcon sentiment={a.sentiment} />
                    <div className="flex-1">
                      <div className="flex items-center gap-2 mb-1">
                        <span className="font-bold text-lg">{a.sentiment || "neutralny"}</span>
                        <Badge className={`text-xs ${
                          a.sentiment === "pozytywny" ? "bg-green-100 text-green-700" :
                          a.sentiment === "negatywny" ? "bg-red-100 text-red-700" :
                          "bg-amber-100 text-amber-700"
                        }`}>
                          {a.sentiment_score || 50}/100
                        </Badge>
                        <Badge variant="outline" className="text-xs">{result.tweets_found || 0} tweetow</Badge>
                      </div>
                      <SentimentBar score={a.sentiment_score || 50} />
                      <p className="text-sm text-muted-foreground mt-2">{a.summary}</p>
                    </div>
                  </div>
                </CardContent>
              </Card>

              {/* Key opinions + Ideas */}
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                {a.key_opinions?.length > 0 && (
                  <Card>
                    <CardHeader className="pb-2">
                      <CardTitle className="text-sm flex items-center gap-1.5">
                        <MessageCircle className="w-4 h-4 text-sky-500" /> Kluczowe opinie
                      </CardTitle>
                    </CardHeader>
                    <CardContent className="space-y-1.5">
                      {a.key_opinions.map((o, i) => (
                        <div key={i} className="text-xs bg-muted/30 rounded px-2.5 py-1.5">{o}</div>
                      ))}
                    </CardContent>
                  </Card>
                )}
                {a.content_ideas?.length > 0 && (
                  <Card>
                    <CardHeader className="pb-2">
                      <CardTitle className="text-sm flex items-center gap-1.5">
                        <Lightbulb className="w-4 h-4 text-amber-500" /> Pomysly na artykuly
                      </CardTitle>
                    </CardHeader>
                    <CardContent className="space-y-1.5">
                      {a.content_ideas.map((idea, i) => (
                        <div key={i} className="text-xs bg-amber-50 rounded px-2.5 py-1.5">{idea}</div>
                      ))}
                    </CardContent>
                  </Card>
                )}
              </div>

              {/* Tweets */}
              {result.tweets?.length > 0 && (
                <Card>
                  <CardHeader className="pb-2">
                    <CardTitle className="text-sm">Tweety ({result.tweets.length})</CardTitle>
                  </CardHeader>
                  <CardContent className="p-0">
                    <ScrollArea className="h-[350px]">
                      {result.tweets.map((t, i) => (
                        <div key={i} className="px-4 py-3 border-b hover:bg-muted/20" data-testid={`tweet-${i}`}>
                          <div className="flex items-start gap-2">
                            <div className="w-8 h-8 rounded-full bg-sky-100 flex items-center justify-center shrink-0">
                              <Twitter className="w-4 h-4 text-sky-600" />
                            </div>
                            <div className="flex-1 min-w-0">
                              <div className="flex items-center gap-1.5">
                                <span className="text-xs font-bold">@{t.username}</span>
                                {t.date && <span className="text-[10px] text-muted-foreground">{t.date}</span>}
                              </div>
                              <p className="text-xs mt-0.5 text-foreground/80">{t.text}</p>
                              <div className="flex items-center gap-3 mt-1.5 text-[10px] text-muted-foreground">
                                <span className="flex items-center gap-0.5"><Heart className="w-3 h-3" />{t.likes || 0}</span>
                                <span className="flex items-center gap-0.5"><Repeat2 className="w-3 h-3" />{t.retweets || 0}</span>
                                <span className="flex items-center gap-0.5"><MessageCircle className="w-3 h-3" />{t.replies || 0}</span>
                                {t.url && (
                                  <a href={t.url} target="_blank" rel="noopener noreferrer" className="text-sky-600 hover:underline ml-auto">Otworz</a>
                                )}
                              </div>
                            </div>
                          </div>
                        </div>
                      ))}
                    </ScrollArea>
                  </CardContent>
                </Card>
              )}
            </>
          )}

          {!result && !analyzing && (
            <Card>
              <CardContent className="p-12 text-center text-muted-foreground">
                <Twitter className="w-16 h-16 mx-auto mb-4 opacity-20" />
                <p className="text-sm">Wpisz temat aby przeanalizowac dyskusje na X/Twitter</p>
                <p className="text-xs mt-1">Np. "sztuczna inteligencja Polska", "bitcoin kurs", "inflacja 2026"</p>
              </CardContent>
            </Card>
          )}
        </div>

        {/* History */}
        <Card>
          <CardHeader className="pb-2">
            <CardTitle className="text-sm flex items-center gap-1.5">
              <Clock className="w-4 h-4" /> Historia analiz
            </CardTitle>
          </CardHeader>
          <CardContent className="p-0">
            <ScrollArea className="h-[500px]">
              {loadingHistory && <div className="p-4 text-center"><Loader2 className="w-5 h-5 animate-spin mx-auto" /></div>}
              {history.length === 0 && !loadingHistory && (
                <div className="p-4 text-center text-xs text-muted-foreground">Brak historii</div>
              )}
              {history.map((item, i) => (
                <div
                  key={i}
                  className="px-4 py-2.5 border-b cursor-pointer hover:bg-muted/30 transition-colors"
                  onClick={() => loadFromHistory(item)}
                  data-testid={`history-item-${i}`}
                >
                  <div className="text-sm font-medium truncate">{item.query}</div>
                  <div className="flex items-center gap-2 mt-0.5">
                    <Badge className={`text-[8px] ${
                      item.analysis?.sentiment === "pozytywny" ? "bg-green-100 text-green-700" :
                      item.analysis?.sentiment === "negatywny" ? "bg-red-100 text-red-700" :
                      "bg-amber-100 text-amber-700"
                    }`}>
                      {item.analysis?.sentiment || "?"}
                    </Badge>
                    <span className="text-[10px] text-muted-foreground">{item.tweets_found || 0} tweetow</span>
                    <span className="text-[10px] text-muted-foreground ml-auto">{item.scraped_at?.substring(0, 10)}</span>
                  </div>
                </div>
              ))}
            </ScrollArea>
          </CardContent>
        </Card>
      </div>
    </div>
  );
}
